import React from "react";

export const translationColumns = [
  { id: "lang", label: "Language", alignHead: "center", alignBody: "center" },
  {
    id: "inputValue",
    label: "Translation",
    alignHead: "center",
    alignBody: "center",
  },
  { id: "actions", label: "Actions", alignHead: "center", alignBody: "center" },
];

export const playerLabel = (player: any) =>
  `${player.playerName || "Unnamed"}${player.isDemo ? " (demo)" : ""} - ${player.rgsPlayerId}${player.isUnderMaintenance ? " [Under Maintenance]" : ""}`;

interface UseTranslationModalArgs {
  details: any;
  setDetails: React.Dispatch<React.SetStateAction<any>>;
  open: () => void;
  close: () => void;
  showSnackbar: (message?: string, status?: string) => void;
}

/** Mirrors the notifications editor wiring so the modal behaves identically across the CMS. */
export const useTranslationModal = ({
  details,
  setDetails,
  open,
  close,
  showSnackbar,
}: UseTranslationModalArgs) => {
  const [modalData, setModalData] = React.useState<any>({
    modal: "",
    language: "",
    inputValue: "",
    field: null,
    viewData: [],
  });

  const getTranslatedData = (field: string) =>
    Object.entries(details?.[field] || {}).map(([lang, inputValue]) => ({
      lang,
      inputValue,
    }));

  const handleOpenModal = (type: string, field: string) => {
    setModalData((state: any) =>
      type === "view"
        ? { ...state, viewData: getTranslatedData(field), field, modal: type }
        : { ...state, field, modal: type, language: "", inputValue: "" },
    );
    open();
  };

  const handleChange = (event: any) => {
    const { name, value } = event.target;
    setModalData((prev: any) => ({ ...prev, [name]: value }));
  };

  const handleAddTranslation = () => {
    const { field, language, inputValue } = modalData;

    if (!field || !language) {
      showSnackbar("Select a language before adding the translation", "error");
      return;
    }

    setDetails((prev: any) => ({
      ...prev,
      [field]: { ...prev[field], [language]: inputValue },
      // en doubles as the base field, same as the notifications editor.
      ...(language === "en" && inputValue !== ""
        ? { [field.split("Trans")[0]]: inputValue }
        : {}),
    }));

    setModalData((prev: any) => ({
      ...prev,
      inputValue: "",
      language: "",
      field: null,
    }));
    close();
  };

  const handleLangEditClick = (data: any) => {
    close();
    setModalData((state: any) => ({
      ...state,
      language: data?.lang,
      inputValue: data?.inputValue,
      modal: "add",
    }));
    open();
  };

  const handleLangDeleteClick = (data: any) => {
    if (data.lang === "en") {
      showSnackbar(
        "Cannot delete English translation, as it's a default language",
        "error",
      );
      return;
    }

    const field = modalData.field;

    setModalData((state: any) => ({
      ...state,
      viewData: state.viewData.filter((item: any) => item.lang !== data.lang),
    }));

    setDetails((prev: any) => {
      const next = { ...prev[field] };
      delete next[data.lang];
      return { ...prev, [field]: next };
    });
  };

  /** A language added but left blank is not a translation, and would override the fallback. */
  const cleanedTrans = (field: string) => {
    const entries = Object.entries(details?.[field] || {}).filter(
      ([, value]) => String(value ?? "").trim() !== "",
    );
    return entries.length ? Object.fromEntries(entries) : undefined;
  };

  return {
    modalData,
    handleOpenModal,
    handleChange,
    handleAddTranslation,
    handleLangEditClick,
    handleLangDeleteClick,
    cleanedTrans,
  };
};